import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert, Box, Button, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { getVideoOps, getVideoOpsAudit } from '../api';
import { useEnv } from '../contexts/EnvContext';
import PageHeader from './common/PageHeader';
import StatCard from './common/StatCard';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';
import { ENVIRONMENT_NAMES } from './StuckClocks';

const when = (d) => (d ? dayjs(d).format('MMM D, h:mm A') : '—');
const tokens = (n) => `${Number(n || 0).toLocaleString()} ${Number(n) === 1 ? 'token' : 'tokens'}`;
const payments = (n) => `${Number(n || 0).toLocaleString()} ${Number(n) === 1 ? 'payment' : 'payments'}`;
const UNREAD = 'Bidsy could not read this; its log says why.';

// The environment a request goes to is read from storage at send time (api.js), so an answer for another
// environment than this tab shows means it was switched in another tab.
const OTHER_TAB = 'The environment was switched in another tab. Reload the page.';

const failureOf = (e, fallback) => {
  const body = e?.response?.data;
  if (e?.response?.status === 404) return { notice: body?.message || 'Not on this environment yet.' };
  return { error: body?.message || e?.message || fallback };
};

// One read of the daily line as a tile; a read Bidsy could not make is shown as such, never as 0.
function MoneyTile({ title, part, help, warnWhen = false }) {
  if (part == null) return <StatCard title={title} value="—" helpText={UNREAD} color="error" />;
  return <StatCard title={title} value={tokens(part.tokens)} helpText={help || payments(part.payments)} color={warnWhen ? 'warning' : 'primary'} />;
}

function AuditRows({ label, rows }) {
  const columns = Object.keys(rows[0] || {});
  return (
    <Box sx={{ overflowX: 'auto', mb: 1 }}>
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>{columns.map((column) => <TableCell key={column}>{column}</TableCell>)}</TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {columns.map((column) => <TableCell key={column}>{row[column] == null ? '—' : String(row[column])}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/**
 * The money that waits (admin panel plan RPT-33): the reads of the daily
 * operations line, as Bidsy's route 47 answers them, and the payments audit
 * on demand. Nothing on it is a deadline.
 */
export default function VideoMoney() {
  const { env } = useEnv();
  const [view, setView] = useState({ loading: true, data: null, error: null, notice: null });
  const [audit, setAudit] = useState({ running: false, data: null, error: null, notice: null });
  const latest = useRef(0);
  const latestAudit = useRef(0);

  const load = useCallback(async () => {
    const asked = ++latest.current;
    setView((prev) => ({ loading: true, data: prev.data, error: null, notice: null }));
    try {
      const answer = await getVideoOps();
      if (asked !== latest.current) return;
      if (answer.data?.environment !== env) setView({ loading: false, data: null, error: OTHER_TAB, notice: null });
      else setView({ loading: false, data: answer.data, error: null, notice: null });
    } catch (e) {
      if (asked === latest.current) setView({ loading: false, data: null, error: null, notice: null, ...failureOf(e, 'Could not load the money page') });
    }
  }, [env]);

  useEffect(() => {
    // Another environment's figures and audit are not shown while this one loads.
    latestAudit.current += 1;
    setAudit({ running: false, data: null, error: null, notice: null });
    setView({ loading: true, data: null, error: null, notice: null });
    load();
  }, [load, env]);

  const runAudit = async () => {
    const asked = ++latestAudit.current;
    setAudit({ running: true, data: null, error: null, notice: null });
    try {
      const answer = await getVideoOpsAudit();
      if (asked !== latestAudit.current) return;
      if (answer.data?.environment !== env) setAudit({ running: false, data: null, error: OTHER_TAB, notice: null });
      else setAudit({ running: false, data: { ...answer.data, readAt: new Date() }, error: null, notice: null });
    } catch (e) {
      if (asked === latestAudit.current) setAudit({ running: false, data: null, error: null, notice: null, ...failureOf(e, 'The audit did not run') });
    }
  };

  const ops = view.data;
  const open = ops?.openCases;
  const weekly = ops?.weeklyNoCallPayoutsByCreator;
  const wins = ops?.unconvertedAuctionWins;
  // "Found nothing" only from the sections themselves, every one read and at 0.
  const sections = Array.isArray(audit.data?.sections) && audit.data.sections.length ? audit.data.sections : null;
  const found = sections ? sections.reduce((sum, section) => sum + Number(section.count), 0) : null;
  const where = (data) => ENVIRONMENT_NAMES[data.environment] || data.environment;

  return (
    <>
      <PageHeader
        title="Video money"
        subtitle={ops ? `Read on ${where(ops)} at ${when(ops.at)}. What waits for a person or a release time; nothing here is a deadline.` : 'What waits for a person or a release time'}
      />
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }} aria-busy={view.loading || audit.running}>
        <Button variant="outlined" onClick={load} disabled={view.loading}>Refresh</Button>
        <Button variant="contained" onClick={runAudit} disabled={audit.running}>{audit.running ? 'Auditing…' : 'Audit payments'}</Button>
      </Paper>

      {view.loading && !ops ? <LoadingBlock height={240} /> : null}
      {view.error ? <ErrorBlock message={view.error} onRetry={load} /> : null}
      {view.notice ? <Alert severity="info" sx={{ mb: 2 }}>{view.notice}</Alert> : null}

      {audit.error ? <ErrorBlock message={audit.error} onRetry={runAudit} /> : null}
      {audit.notice ? <Alert severity="info" sx={{ mb: 2 }}>{audit.notice}</Alert> : null}
      {audit.data ? (
        <Paper sx={{ p: 2, mb: 2 }} data-testid="payments-audit">
          <Alert severity={found === 0 ? 'success' : 'warning'} sx={{ mb: 1 }}>
            {`Audited ${where(audit.data)} at ${dayjs(audit.data.readAt).format('h:mm A')}: `}
            {found == null || Number.isNaN(found) ? "Bidsy's answer was not understood. Tell a developer." : null}
            {found === 0 ? 'the audit found nothing.' : null}
            {found > 0 ? `the audit found ${found.toLocaleString()} ${found === 1 ? 'finding' : 'findings'}. Tell a developer.` : null}
          </Alert>
          {(sections || []).map((section) => (
            <Box key={section.key} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: section.count ? 700 : 400 }}>
                {section.key}. {section.title}: {Number(section.count).toLocaleString()}
              </Typography>
              {section.count ? (
                <>
                  {section.count > (section.rows || []).length ? (
                    <Typography variant="caption" color="text.secondary">The first {(section.rows || []).length} of {Number(section.count).toLocaleString()}</Typography>
                  ) : null}
                  <AuditRows label={`audit section ${section.key}`} rows={section.rows || []} />
                </>
              ) : null}
            </Box>
          ))}
        </Paper>
      ) : null}

      {ops ? (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              {open == null ? <StatCard title="Open cases" value="—" helpText={UNREAD} color="error" /> : (
                <StatCard
                  title="Open cases"
                  value={open.underOneDay + open.oneToSevenDays + open.overSevenDays}
                  helpText={<>{open.oldestDays == null ? 'None open' : `The oldest ${open.oldestDays ? `${open.oldestDays} days` : 'under a day'}`} · <Link to="/video-cases">The queue</Link></>}
                  breakdown={[
                    { label: 'Under 1 day', value: open.underOneDay },
                    { label: '1 to 7 days', value: open.oneToSevenDays },
                    { label: 'Over 7 days', value: open.overSevenDays },
                  ]}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}><MoneyTile title="Held by cases" part={ops.heldByCases} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MoneyTile title="Frozen by reports" part={ops.frozenByReports} /></Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MoneyTile
                title="Pay pending release"
                part={ops.payPending}
                help={ops.payPending ? `${payments(ops.payPending.payments)} earned, not yet overdue` : null}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MoneyTile
                title="Past release time"
                part={ops.payOverdue}
                warnWhen={ops.payOverdue?.payments > 0}
                help={ops.payOverdue ? `${payments(ops.payOverdue.payments)} over 5 minutes past release that nothing froze${ops.payOverdue.payments ? ': tell a developer' : ''}` : null}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {ops.stuckClocks == null ? <StatCard title="Stuck clocks" value="—" helpText={UNREAD} color="error" /> : (
                <StatCard
                  title="Stuck clocks"
                  value={ops.stuckClocks}
                  color={ops.stuckClocks > 0 ? 'warning' : 'primary'}
                  helpText={ops.stuckClocks > 0 ? <Link to="/">Run again from the dashboard</Link> : null}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}><MoneyTile title="Last day: paid, no completed call" part={ops.lastDay?.noCallPayouts} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MoneyTile title="Last day: kept from no-shows" part={ops.lastDay?.keptFromNoShows} /></Grid>
          </Grid>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Paid with no completed call, last 7 days, by creator</Typography>
            {weekly == null ? <Alert severity="error">{UNREAD}</Alert> : null}
            {weekly && !weekly.length ? <Typography variant="body2" color="text.secondary">None in the last 7 days.</Typography> : null}
            {weekly?.length ? (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="weekly payouts by creator">
                  <TableHead>
                    <TableRow>
                      <TableCell>Creator</TableCell>
                      <TableCell align="right">Payouts</TableCell>
                      <TableCell align="right">Tokens</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {weekly.map((row) => (
                      <TableRow key={row.creatorId} hover>
                        <TableCell><Link to={`/user/${row.creatorId}`}>{row.username || `User #${row.creatorId}`}</Link></TableCell>
                        <TableCell align="right">{row.payouts.toLocaleString()}</TableCell>
                        <TableCell align="right">{row.tokens.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : null}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Video auction wins that never became a request</Typography>
            {wins == null ? <Alert severity="error">{UNREAD}</Alert> : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {wins.auctions
                  ? `${wins.auctions.toLocaleString()} more than 48 hours after their period ended, ${tokens(wins.tokens)}.${wins.auctions > (wins.named || []).length ? ` The first ${(wins.named || []).length} are named.` : ''} Their tokens stay where they are until someone acts.`
                  : 'None.'}
              </Typography>
            )}
            {wins?.named?.length ? (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="unconverted auction wins">
                  <TableHead>
                    <TableRow>
                      <TableCell>Auction</TableCell>
                      <TableCell>Winning bid</TableCell>
                      <TableCell>Auction status</TableCell>
                      <TableCell align="right">Tokens</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wins.named.map((row) => (
                      <TableRow key={row.auctionId} hover>
                        <TableCell>#{row.auctionId}</TableCell>
                        <TableCell>#{row.bidId}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell align="right">{row.tokens.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : null}
          </Paper>
        </>
      ) : null}
    </>
  );
}
